<template>
  <div>
    <!-- Search and Status -->
    <v-row align="center">
      <v-col cols="12" xl="3" lg="3" md="3" sm="4" xs="12">
        <SearchText
          :text="'Search by title'"
          :value="'title'"
          @searchTextChanged="searchTextChanged"
          @keypress.enter.native.prevent="triggerSearch"
          @triggerSearch="triggerSearch"
          @triggerClear="triggerClear"
        ></SearchText>
      </v-col>
      <v-col class="d-flex align-center justify-end">
        <v-btn @click="resetStatus" class="font-weight-black mx-5" medium plain>
          Reset
        </v-btn>
        <v-select
          v-model="status"
          :items="itemsStatus"
          dense
          style="max-width: 245px"
          outlined
          label="Status"
          rounded
          hide-details
          return-object
          @input="changeStatus"
        >
        </v-select>
      </v-col>
    </v-row>
    <v-row v-if="orders.length === 0">
      <v-col cols="12" class="text-center">
        There are not any orders currently.
      </v-col>
    </v-row>

    <v-row class="mt-10">
      <v-col cols="12" v-for="(order, index) in orders" :key="'order-' + index">
        <v-card style="border-radius: 24px;" elevation="3">
          <v-card-text style="background: var(--v-primary50-base) !important;">
            <v-row>
              <v-col cols="auto" class="d-flex align-center">
                <div>
                  <v-chip flat label small color="blue" class="white--text font-weight-bold">
                    Patent:
                  </v-chip>
                </div>
                <label class="ml-5">
                  {{ order.patent.name }}
                </label>
              </v-col>
              <v-col cols="auto" class="d-flex align-center">
                <div>
                  <v-chip flat label small color="blue" class="white--text font-weight-bold">
                    Provider:
                  </v-chip>
                </div>
                <label class="ml-5">
                  <router-link
                    :to="{
                      name: 'OverviewProvider',
                      params: { id: order.patent.organisation.id },
                      query: { patentOrdersQuery: true },
                    }"
                    style="text-decoration: underline; color: inherit;"
                  >
                    {{ order.patent.organisation.name }}
                  </router-link>
                </label>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="auto" class="d-flex align-center justify-end">
                <div>
                  Estimated Between:
                </div>
                <label class="ml-2">
                  {{ formatDate(order.fromDate) }} - {{ formatDate(order.toDate) }}
                </label>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-text>
            <v-row>
              <v-col cols="3">
                <label>Requested</label>
                <div>{{ formatDateTime(order.createdTimestamp) }}</div>
              </v-col>
              <v-col cols="3">
                <label>Provider Review</label>
                <div>{{ formatDateTime(order.updatedByProvider) }}</div>
              </v-col>
              <v-col cols="3" align="end" class="text-center">
                <label>Researcher Review</label>
                <div>{{ formatDateTime(order.updatedByResearcher) }}</div>
              </v-col>
              <v-col cols="3" align="end" v-if="order.status.id === 'INVALIDATED_BY_PROVIDER'">
                <label style="color: red; font-weight: bold;">
                  Invalidated
                </label>
                <div>{{ formatDateTime(order.completedTimestamp) }}</div>
              </v-col>
              <v-col cols="3" align="end" v-else>
                <label
                  :style="
                    order.status.id === 'COMPLETED_BY_PROVIDER'
                      ? { color: 'green', fontWeight: 'bold' }
                      : {}
                  "
                >
                  Completed
                </label>
                <div>
                  {{ formatDateTime(order.completedTimestamp) }}
                </div>
              </v-col>
            </v-row>
            <div style="position: relative;">
              <v-progress-linear
                :value="getProgress(order)"
                color="green"
                id="equipment-progress-bar"
                height="25"
                rounded
                class="mb-4 mt-4"
              >
              </v-progress-linear>
              <v-icon
                v-if="
                  order.status.id === 'REJECTED_BY_PROVIDER' ||
                    order.status.id === 'REJECTED_BY_RESEARCHER' ||
                    order.status.id === 'INVALIDATED_BY_PROVIDER'
                "
                small
                :style="{
                  position: 'absolute',
                  left: `${getProgress(order) - 0.5}%`,
                  transform: 'translateX(-50%)',
                  top: '-1px',
                  color: 'white !important',
                }"
              >
                mdi-close-circle
              </v-icon>
              <v-icon
                v-if="
                  order.status.id === 'ACCEPTED_BY_PROVIDER' ||
                    order.status.id === 'ACCEPTED_BY_RESEARCHER' ||
                    order.status.id === 'COMPLETED_BY_PROVIDER'
                "
                small
                :style="{
                  position: 'absolute',
                  left: `${getProgress(order) - 0.5}%`,
                  transform: 'translateX(-50%)',
                  top: '-1px',
                  color: 'black !important',
                }"
              >
                mdi-check-circle
              </v-icon>
            </div>
            <v-row>
              <v-spacer></v-spacer>
              <v-col align="start" cols="6" v-if="order.status.id === 'REJECTED_BY_PROVIDER'">
                <div style="color: red; font-weight: bold;">Rejected</div>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="5" v-if="order.status.id === 'REJECTED_BY_RESEARCHER'">
                <div style="color: red; font-weight: bold;">Rejected</div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-text>
            <v-row>
              <v-col cols="auto">
                <div>
                  User:
                </div>
                <label>
                  {{ order.user.fullName }}
                </label>
              </v-col>
              <v-col class="mx-10">
                <div>
                  Order Date:
                </div>
                <label>
                  {{ formatDateTime(order.createdTimestamp) || '-' }}
                </label>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Pagination -->
    <v-row v-if="pagesLength > 1">
      <v-spacer></v-spacer>
      <v-col cols="4" md="2" class="justify-end d-flex flex-row align-center">
        <div>{{ totalItems }} Results</div>
      </v-col>
      <v-col cols="8" md="2" class="justify-end d-flex flex-row align-center">
        <div class="pr-2">Show:</div>
        <div style="max-width: 90px">
          <v-select
            v-model="options.itemsPerPage"
            :items="[10, 20, 30, 40, 50, 100]"
            dense
            outlined
            hide-details
            @change="onItemsPerPageChange"
          ></v-select>
        </div>
      </v-col>
      <v-col cols="12" md="6" class="justify-end d-flex flex-row align-center">
        <v-pagination
          v-model="options.page"
          :length="pagesLength"
          :total-visible="7"
          @input="onPageChange"
        ></v-pagination>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import SearchText from '@/components/shared/search/SearchText';

export default {
  name: 'MyOrdersPatentHostingRepresentative',
  components: { SearchText },
  mixins: [util],
  props: {
    search: Object,
    searchAttributes: Array,
  },
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      orders: [],
      totalItems: 0,
      pagesLength: null,
      status: null,
      itemsStatus: [
        { text: 'Requested', value: 'REQUESTED', desc: true },
        { text: 'Completed', value: 'COMPLETED_BY_PROVIDER', desc: true },
        { text: 'Invalidated', value: 'INVALIDATED_BY_PROVIDER', desc: true },
        { text: 'Accepted by Provider', value: 'ACCEPTED_BY_PROVIDER', desc: true },
        { text: 'Rejected by Provider', value: 'REJECTED_BY_PROVIDER', desc: true },
        { text: 'Accepted by Researcher', value: 'ACCEPTED_BY_RESEARCHER', desc: true },
        { text: 'Rejected by Researcher', value: 'REJECTED_BY_RESEARCHER', desc: true },
      ],
    };
  },
  mounted() {
    this.getOrders();

    if (this.searchAttributes) {
      for (let i = 0; i < this.searchAttributes.length; i += 1) {
        const attr = this.searchAttributes[i];
        if (this.$route.query[attr]) {
          this.$set(this.search, attr, this.$route.query[attr]);
        }
      }
    }
  },
  methods: {
    searchTextChanged(obj) {
      console.log('[Table] searchTextChanged');
      this.search[obj.value] = obj.search;
      console.log(this.search);
    },
    triggerSearch() {
      console.log('[Table] Search watch');
      this.options.page = 1;
      this.getOrders();
    },
    triggerClear() {
      console.log('[Table] Clear watch');

      this.search = {};

      this.options.page = 1;
      this.getOrders();
    },
    changeStatus() {
      this.options.page = 1;
      this.getOrders();
    },
    resetStatus() {
      this.status = null;
      this.options.page = 1;
      this.getOrders();
    },
    getOrders() {
      console.log('[Table] Loading data');
      this.loading = true;
      EventBus.$emit('waiting', true);

      this.axios
        .get(`api/v1/orders-patent/institution-representative`, {
          params: this.getParams(),
        })
        .then(res => {
          if (res.status === 200) {
            console.log('Orders fetched successfully:', res.data);
            this.orders = res.data.content;
            this.totalItems = res.data.total;
            this.pagesLength = res.data.pagesLength;
          }
        })
        .catch(error => {
          console.error('Error', error.res);
        })
        .finally(() => {
          this.loading = false;
          EventBus.$emit('waiting', false);
        });
    },
    onItemsPerPageChange() {
      this.options.page = 1;
      this.getOrders();
    },
    onPageChange(newPage) {
      this.options.page = newPage;
      this.getOrders();
    },
    getParams() {
      const { page, itemsPerPage } = this.options;

      const query = {
        index: itemsPerPage === -1 ? 0 : page - 1,
        size: itemsPerPage === -1 ? 1000 : itemsPerPage,
      };

      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.search[attr]) {
            query[attr] = this.search[attr];
          }
        }
      }

      if (this.status && this.status.value) {
        query.status = this.status.value;
      }

      return query;
    },
    getProgress(order) {
      switch (order.status.id) {
        case 'REQUESTED':
          return 0;
        case 'ACCEPTED_BY_PROVIDER':
          return 33;
        case 'REJECTED_BY_PROVIDER':
          return 33;
        case 'ACCEPTED_BY_RESEARCHER':
          return 66;
        case 'REJECTED_BY_RESEARCHER':
          return 66;
        case 'COMPLETED_BY_PROVIDER':
          return 100;
        case 'INVALIDATED_BY_PROVIDER':
          return 100;
      }
    },
  },
};
</script>
